// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { RouteType, SearchScopeType } from '@Types';
import ListProvider from '@Context/ListProvider';
import SearchFilter from '@Organisms/Search/SearchFilter/SearchFilter';
import SearchResults from '@Organisms/Search/SearchResults/SearchResults';
import SearchScope from '@Organisms/Search/SearchScope/SearchScope';
import { ListItemsPerPageType } from '@Constants/pagination';
import RouteBuilder from '@Services/RouteBuilder';
import { Link, useLocation } from 'react-router-dom';
import { useLocalMessage } from '@Helpers/locale';
import usePagination from '@Hooks/usePagination';
import useStorageSearch from '@Hooks/useStorageSearch';
import useFilters from '@Hooks/useFilters';
import { computeEventList, SearchOnListParams } from '@Organisms/Search/eventList';
import client, { queryClient } from '@Client';
import { useListAllFields } from '@Queries/describeQueries';
import { useMemo } from 'react';
import { ICONS } from '@Constants/iconsApp';
import { FilterInput, ObjectTypeV1, SearchResultsType } from 'thehive-sdk';
import usePageTitle from '@Hooks/usePageTitle';

import './SearchPage.scss';

const extraDataByScope: Partial<Record<SearchScopeType, string[]>> = {
    task: ['case'],
    log: ['task'],
    observable: ['links'],
    case: ['status'],
    alert: ['status'],
    job: ['links'],
};

const optionsByScope: Partial<Record<SearchScopeType, Record<string, any>>> = {};

const SearchHome = (): ReactElement => {
    const i = useLocalMessage();
    const { search } = useLocation();
    const query = useMemo(() => new URLSearchParams(search), [search]);
    const fetchQuery = (query.get('fetch') || 'false') === 'true';

    const { page, itemsPerPage, setPagination } = usePagination();

    const { data: describe } = useListAllFields();

    const filterManager = useFilters();

    const { tempFilters = [], setFilters, setTempFilters, clearFilters, addTempFilter } = filterManager;

    const {
        activeScope,
        filters: cacheFilters,
        setSearchActiveScope,
        setSearchFilters,
        keywords: searchKeywords,
        setSearchKeyword,
    } = useStorageSearch();

    const [fieldScope, setFieldScope] = useState<SearchScopeType | undefined>(activeScope);
    const [resultList, setResultList] = useState<SearchResultsType[]>([]);
    const [resultCount, setResultCount] = useState<number>(0);
    const [queryLoading, setQueryLoading] = useState(false);
    const [keyword, setKeyword] = useState('');

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.SearchController,
            }),
        [],
    );

    const refetchSearch = useCallback(
        async (params: SearchOnListParams, keyword: string) => {
            let count = 0;
            const { filters, itemsPerPage, page, queryName, extraData, options } = params;

            if (!fieldScope) {
                return;
            }

            if (filters?.length || keyword) {
                setQueryLoading(true);
                const list = await eventList.onList<SearchResultsType>({
                    queryName: queryName,
                    filters: keyword
                        ? [
                              {
                                  field: 'keyword',
                                  type: 'string',
                                  values: [keyword],
                                  operator: 'any',
                              },
                              ...filters,
                          ]
                        : filters,
                    itemsPerPage,
                    page,
                    extraData,
                    options,
                });
                setResultList(list);

                if (list?.length > 0) {
                    count = await eventList.onCount({
                        queryName: queryName,
                        filters: keyword
                            ? [
                                  {
                                      field: 'keyword',
                                      type: 'string',
                                      values: [keyword],
                                      operator: 'any',
                                  },
                                  ...filters,
                              ]
                            : filters,
                        options,
                        customUrl: keyword ? '/api/v1/search/count' : undefined,
                    });
                }

                setResultCount(count);
                setQueryLoading(false);
            }
        },
        [eventList, fieldScope],
    );

    const onSearch = useCallback(
        (keyword: string): void => {
            if (activeScope) {
                setSearchKeyword(activeScope, keyword);

                const filtersParsed = tempFilters.map((filter) => ({
                    ...filter,
                    describeAttribute: describe?.[activeScope].attributes?.find(
                        ({ name }) => name === filter.field,
                    ) as any,
                }));

                setFilters(filtersParsed);

                setSearchFilters(activeScope, filtersParsed as FilterInput[]);

                if (!describe || !fieldScope) {
                    return;
                }

                refetchSearch(
                    {
                        queryName: describe[fieldScope].initialQuery,
                        page,
                        itemsPerPage,
                        filters: (filtersParsed as FilterInput[]) || [],
                        extraData: extraDataByScope[fieldScope] || [],
                        options: optionsByScope[fieldScope] || undefined,
                    },
                    keyword,
                );
            }
        },
        [activeScope, setFilters, setSearchFilters, tempFilters, describe, setSearchKeyword, keyword], // eslint-disable-line
    );

    const onClear = useCallback((): void => {
        if (activeScope) {
            clearFilters();
            setSearchFilters(activeScope, []);
            setSearchKeyword(activeScope, '');
            setKeyword('');
            queryClient.removeQueries(['countResults', activeScope]);
            queryClient.removeQueries(['getResults', activeScope]);
        }
    }, [activeScope, clearFilters, setSearchFilters, setSearchKeyword]);

    const onAdd = useCallback((): void => {
        if (activeScope) {
            addTempFilter({});
        }
    }, [activeScope, addTempFilter]);

    const onPageChange = useCallback(
        (page: number): void => {
            setPagination((prevValues) => ({ ...prevValues, page }));
        },
        [setPagination],
    );

    const onItemsPerPageChange = useCallback(
        (itemsPerPage: ListItemsPerPageType): void => {
            setPagination({ itemsPerPage, page: 0 });
        },
        [setPagination],
    );

    useEffect(() => {
        if (!fetchQuery) {
            return;
        }

        if (!fieldScope || !describe || !describe[fieldScope]?.initialQuery) {
            return;
        }

        if (!cacheFilters?.[fieldScope]?.length && !searchKeywords[fieldScope]) {
            return;
        }

        refetchSearch(
            {
                queryName: describe[fieldScope].initialQuery,
                page,
                itemsPerPage,
                filters: cacheFilters[fieldScope] || [],
                extraData: extraDataByScope[fieldScope] || [],
                options: optionsByScope[fieldScope] || undefined,
            },
            searchKeywords[fieldScope],
        );
    }, [fieldScope, fetchQuery, describe]); // eslint-disable-line

    useEffect(() => {
        if (fieldScope) {
            setSearchActiveScope(fieldScope);
            setPagination((prevValues) => ({ ...prevValues, page: 0 }));
            setFilters(cacheFilters[fieldScope]);
            setTempFilters(cacheFilters[fieldScope]);
            setKeyword(searchKeywords[fieldScope]);

            setResultList([]);
            setResultCount(0);
        }
    }, [fieldScope, cacheFilters, describe]); //eslint-disable-line

    useEffect(() => {
        const keyword = query.get('keyword');

        if (keyword) {
            setSearchKeyword(ObjectTypeV1.ANY, keyword);
            setKeyword(keyword);
            setFieldScope(ObjectTypeV1.ANY);
            setSearchActiveScope(ObjectTypeV1.ANY);
        }
    }, []); // eslint-disable-line

    usePageTitle(i('page-title.search'));

    return (
        <div className="search-page">
            <div className="search-page__container">
                <SearchScope fieldScope={fieldScope} setFieldScope={setFieldScope} />
                <SearchFilter
                    keyword={keyword}
                    onKeywordChange={(value) => setKeyword(value)}
                    fieldScope={fieldScope}
                    events={{
                        onSearch,
                        onClear,
                        onAdd,
                    }}
                    filterManager={filterManager}
                    loading={queryLoading}
                />
                <SearchResults
                    fieldScope={fieldScope}
                    resultList={resultList}
                    countResults={resultCount}
                    pagination={{
                        page,
                        itemsPerPage,
                        onPageChange: (page): void => {
                            onPageChange(page);
                            if (describe && fieldScope && describe[fieldScope]?.initialQuery) {
                                refetchSearch(
                                    {
                                        queryName: describe[fieldScope].initialQuery,
                                        page,
                                        itemsPerPage,
                                        filters: cacheFilters[fieldScope] || [],
                                        extraData: extraDataByScope[fieldScope] || [],
                                    },
                                    keyword,
                                );
                            }
                        },
                        onItemsPerPageChange: (itemsPerPage): void => {
                            onItemsPerPageChange(itemsPerPage);
                            if (describe && fieldScope && describe[fieldScope]?.initialQuery) {
                                refetchSearch(
                                    {
                                        queryName: describe[fieldScope].initialQuery,
                                        page: 0,
                                        itemsPerPage,
                                        filters: cacheFilters[fieldScope] || [],
                                        extraData: extraDataByScope[fieldScope] || [],
                                    },
                                    keyword,
                                );
                            }
                        },
                    }}
                    loading={queryLoading}
                />
            </div>
        </div>
    );
};

const path = RouteBuilder.compute('search.default');

const MenuLink = (): ReactElement => {
    const local = useLocalMessage();
    return <Link to={path}>{local('mainLayout.userMenu.search')}</Link>;
};

export const Route: RouteType = {
    path,
    menu: {
        key: 'search',
        icon: <ICONS.SearchIcon />,
        render: <MenuLink />,
        selectTab: path,
        group: 'second-space',
    },
    breadcrumb: { label: 'breadcrumb.global.search' },
    Context: ListProvider,
    content: <SearchHome />,
    space: ['user'],
    exact: true,
};
